import React, { useContext, useState } from "react";
import { Eye, EyeOff, KeyRound, Loader2, LogOut, ShieldAlert } from "lucide-react";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useWhiteLabel } from "../../context/WhiteLabel";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import SenhaForte, { avaliarSenha } from "../../components/SenhaForte";

// Tela BLOQUEANTE de troca de senha.
//
// Ela existe para o caso "o admin combinou/gerou a senha e marcou 'exigir troca
// no próximo login'": a pessoa entra com a senha temporária e só chega no
// sistema depois de definir a própria. Enquanto isso não acontece, o backend
// responde 403 em tudo que não é /auth, /settings e a própria troca de senha —
// por isso esta tela vive fora do layout do sistema (routes/Route.jsx).
const TrocarSenha = () => {
	const { user, handleLogout } = useContext(AuthContext);
	const { appName, logoUrl } = useWhiteLabel();

	const [senhaAtual, setSenhaAtual] = useState("");
	const [mostrarAtual, setMostrarAtual] = useState(false);
	const [novaSenha, setNovaSenha] = useState("");
	const [confirmar, setConfirmar] = useState("");
	const [erro, setErro] = useState("");
	const [salvando, setSalvando] = useState(false);

	const enviar = async (e) => {
		e.preventDefault();
		setErro("");

		if (!senhaAtual) {
			setErro("Informe a senha atual para confirmar que é você.");
			return;
		}

		const avaliacao = avaliarSenha(novaSenha, {
			nome: user?.name,
			email: user?.email,
		});
		if (!avaliacao.ok) {
			setErro(avaliacao.erro);
			return;
		}

		if (novaSenha !== confirmar) {
			setErro("As senhas digitadas não conferem.");
			return;
		}

		if (novaSenha === senhaAtual) {
			setErro("A nova senha precisa ser diferente da atual.");
			return;
		}

		setSalvando(true);
		try {
			await api.put(`/users/${user.id}/password`, {
				currentPassword: senhaAtual,
				newPassword: novaSenha,
			});
			toast.success("Senha alterada. Abrindo o sistema…");
			// Recarga proposital: a sessão volta do backend com a exigência já
			// limpa, e a pessoa cai direto no sistema em vez de passar de novo
			// por esta tela.
			window.location.href = "/tickets";
		} catch (err) {
			const codigo = String(err?.response?.data?.error || "");
			if (codigo === "ERR_INVALID_CURRENT_PASSWORD") {
				setErro("A senha atual não confere.");
			} else if (codigo === "ERR_SAME_PASSWORD") {
				setErro("A nova senha precisa ser diferente da atual.");
			} else if (codigo === "ERR_WEAK_PASSWORD") {
				setErro(
					"A nova senha não atende à política de senha. Veja as regras abaixo do campo."
				);
			} else {
				toastError(err);
			}
		} finally {
			setSalvando(false);
		}
	};

	return (
		<div className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
			<div className="w-full max-w-md space-y-5 rounded-xl border border-border bg-card p-6 shadow-lg">
				<div className="flex flex-col items-center gap-2 text-center">
					{logoUrl ? (
						<img
							src={logoUrl}
							alt={appName || "Perabit"}
							className="h-12 object-contain"
						/>
					) : (
						<div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
							<KeyRound className="h-6 w-6 text-primary" />
						</div>
					)}
					<h1 className="text-lg font-semibold">Defina sua nova senha</h1>
					<p className="text-xs text-muted-foreground">
						{user?.name ? `${user.name}, a ` : "A "}
						senha que você usou é temporária. Antes de continuar, escolha
						uma senha só sua.
					</p>
				</div>

				<div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12px] text-amber-700 dark:text-amber-300">
					<ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
					<span>
						O acesso ao sistema fica liberado assim que você salvar a nova
						senha. As outras sessões desta conta serão encerradas por
						segurança.
					</span>
				</div>

				<form onSubmit={enviar} className="space-y-4">
					<div className="space-y-1.5">
						<Label htmlFor="senha-atual">Senha atual (a recebida)</Label>
						<div className="relative">
							<Input
								id="senha-atual"
								type={mostrarAtual ? "text" : "password"}
								autoComplete="current-password"
								autoFocus
								value={senhaAtual}
								onChange={(e) => setSenhaAtual(e.target.value)}
								className="pr-9"
								placeholder="A senha com que você acabou de entrar"
							/>
							<button
								type="button"
								tabIndex={-1}
								onClick={() => setMostrarAtual((v) => !v)}
								aria-label={mostrarAtual ? "Ocultar senha" : "Mostrar senha"}
								className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
							>
								{mostrarAtual ? (
									<EyeOff className="h-4 w-4" />
								) : (
									<Eye className="h-4 w-4" />
								)}
							</button>
						</div>
					</div>

					<SenhaForte
						id="nova-senha"
						label="Nova senha"
						value={novaSenha}
						onChange={setNovaSenha}
						nome={user?.name}
						email={user?.email}
						placeholder="A senha que você vai usar daqui pra frente"
						autoFocus={false}
					/>

					<div className="space-y-1.5">
						<Label htmlFor="confirmar-senha">Confirmar nova senha</Label>
						<Input
							id="confirmar-senha"
							type="password"
							autoComplete="new-password"
							value={confirmar}
							onChange={(e) => setConfirmar(e.target.value)}
							placeholder="Repita a nova senha"
						/>
					</div>

					{erro && <p className="text-xs text-destructive">{erro}</p>}

					<div className="flex flex-col gap-2 sm:flex-row">
						<Button type="submit" className="flex-1" disabled={salvando}>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							Salvar nova senha
						</Button>
						<Button
							type="button"
							variant="outline"
							onClick={handleLogout}
							disabled={salvando}
						>
							<LogOut className="h-4 w-4" />
							Sair
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default TrocarSenha;
